// ============================================
// useChartInteraction — AVWAP, S/R lines, context menu, magnifier
// Owns: overlay state, hover detection, anchor placement, settings menus
// ============================================

import { useState, useEffect, useRef, useCallback } from 'react'
import { RIGHT_PADDING, yToMcap, mcapToY, latticeRowIndex } from '../../utils/chartUtils'
import { resolveEndIdx, startIdxFor } from '../../chart/machines/xWindow'
import { forEachLevel } from '../../utils/candleHelpers'
import { PHONE_HANDLE_HIT, clampHandleX } from '../SRLineSettingsMenu'
import { bucketRowIndex } from '../../utils/chartGeometry'

// Keep in sync with FootprintChartCanvas.jsx line 31 — main chart area reserves
// this much on the right for the futures price-axis overlay (always, even in
// spot mode). All draw x-positioning subtracts it, so hit-testing must too.
const FIXED_OVERLAY_WIDTH = 60

// Stable callback helper (avoids re-creating closures on every render)
function useStableCallback(callback) {
  const callbackRef = useRef(callback)
  callbackRef.current = callback
  return useCallback((...args) => callbackRef.current(...args), [])
}

export { useStableCallback }

export default function useChartInteraction({
  chartData,
  currentToken,
  visibleSortedMcapsRef,
  dynamicRowHeightRef,
  viewEndIndexRef,
  viewWindowSizeRef,
  containerWidthRef,
  fractionalOffsetPxRef, // ← NEW: optional, for pixel-perfect AVWAP hit-testing during micro-scroll
  bodyScrollRef,
  canvasRef,
  drawCanvasRef,
  scheduleDrawRef,     // optional: FPCC's coalesced scheduleCanvasDraw (hover redraws)
  timeIntervalRef,
  showToast,
  saveSRLines,
  srLineMovedRef,      // optional: ref to fn(oldMcap, newMcap) — alerts follow their S/R line
}) {
  // --- AVWAP state ---
  const [avwaps, setAvwaps] = useState([])
  const avwapsRef = useRef(avwaps)
  // (chart-perf) AVWAP hover lives in a REF, not React state. Hover flipped
  // state on every line crossing, which re-rendered the whole FootprintChart
  // and drew twice (sync here + the sync-overlays layout effect). Now the
  // setter writes the ref and queues ONE coalesced canvas draw; the draw loop
  // already reads hoveredAvwapIdRef, so the highlight looks identical.
  const hoveredAvwapIdRef = useRef(null)
  const setHoveredAvwapId = useCallback((id) => {
    if (hoveredAvwapIdRef.current === id) return
    hoveredAvwapIdRef.current = id
    const sched = scheduleDrawRef?.current
    if (sched) sched('full')
    else if (drawCanvasRef.current) drawCanvasRef.current()
  }, [scheduleDrawRef, drawCanvasRef])
  const avwapPointsCacheRef = useRef([]) // legacy — kept so FootprintChartCanvas writes don't break; no longer read for hit-testing
  const [avwapSettings, setAvwapSettings] = useState({ show: false, x: 0, y: 0, vwap: null })
  const nextAvwapId = useRef(1)

  // --- S/R lines state ---
  const [srLines, setSrLines] = useState([])
  const srLinesRef = useRef([])
  const [srLineSettings, setSrLineSettings] = useState({ show: false, x: 0, y: 0, line: null })
  const nextSrLineId = useRef(1)
  const [hoveredSRLineId, setHoveredSRLineId] = useState(null)
  const hoveredSRLineIdRef = useRef(null)

  // --- Unified drag state (AVWAP anchor + S/R line repositioning) ---
  const draggingItemRef = useRef(null)
  const [isDraggingOverlay, setIsDraggingOverlay] = useState(false)

  // --- Context menu ---
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, candleIdx: -1, clickMcap: 0 })

  // --- Y-axis hover / magnifier ---
  const [hoverState, setHoverState] = useState({ rows: [], type: null })
  const [magnifier, setMagnifier] = useState({ visible: false, y: 0, mcap: 0 })
  const magnifierMcapRef = useRef(0)

  // Chart hover state (for TV-style price label — chart area only)
  const [isHoveringChart, setIsHoveringChart] = useState(false)

  // Keep refs in sync
  useEffect(() => { avwapsRef.current = avwaps }, [avwaps])
  useEffect(() => { hoveredSRLineIdRef.current = hoveredSRLineId }, [hoveredSRLineId])
  useEffect(() => { srLinesRef.current = srLines }, [srLines])

  // --- Load S/R lines from localStorage ---
  const loadSRLines = useCallback((token) => {
    try {
      const stored = JSON.parse(localStorage.getItem(`solcheddar_srlines_${token}`) || '[]')
      setSrLines(stored)
      if (stored.length > 0) nextSrLineId.current = Math.max(...stored.map(l => l.id)) + 1
    } catch { setSrLines([]) }
  }, [])

  // (chart2-bounce) Both chart slots can sit on the same token, and each kept
  // its own copy of that token's lines: a save on one overwrote the other's
  // edits in storage and the other chart never saw the change. A save now
  // tells the other slot, which reloads the token's lines from storage.
  const srSyncSrcRef = useRef(null)
  if (srSyncSrcRef.current === null) srSyncSrcRef.current = {}
  const saveSRLinesLocal = useCallback((token, lines) => {
    try { localStorage.setItem(`solcheddar_srlines_${token}`, JSON.stringify(lines)) } catch {}
    setSrLines(lines)
    const src = srSyncSrcRef.current
    queueMicrotask(() => {
      try { window.dispatchEvent(new CustomEvent('cheddar-drawings', { detail: { kind: 'sr', token, src } })) } catch {}
    })
  }, [])
  const srTokenRef = useRef(currentToken)
  srTokenRef.current = currentToken
  useEffect(() => {
    const onSync = (e) => {
      const d = e && e.detail
      if (!d || d.kind !== 'sr' || d.src === srSyncSrcRef.current) return
      if (!d.token || d.token !== srTokenRef.current) return
      if (draggingItemRef.current && draggingItemRef.current.type === 'sr') return   // our own drag owns the lines
      loadSRLines(d.token)
    }
    window.addEventListener('cheddar-drawings', onSync)
    return () => window.removeEventListener('cheddar-drawings', onSync)
  }, [loadSRLines])

  // --- Y-axis row hover ---
  // Read through a ref: the raw price changes every trade, and a callback
  // keyed on it hands every consumer a new function identity per tick.
  const highlightMcapRef = useRef(0)
  highlightMcapRef.current = chartData.highlightMcap
  const handleRowHover = useCallback((mcap) => {
    const mcaps = visibleSortedMcapsRef.current
    const highlightMcap = highlightMcapRef.current
    // Lattice-relative, not indexOf: highlightMcap is the raw last price and
    // is almost never bit-identical to a row key (and never was on a LOG
    // lattice, where the old linear snap missed every key). `mcap` is a
    // hovered axis row, so it IS a key and indexOf is right for it.
    const _ci = bucketRowIndex(mcaps, highlightMcap)
    const currentIndex = (_ci >= 0 && _ci < mcaps.length) ? _ci : -1
    const hoverIndex = mcaps.indexOf(mcap)
    if (currentIndex === -1) return

    if (hoverIndex < currentIndex) {
      setHoverState({ rows: mcaps.slice(hoverIndex, currentIndex), type: 'sell' })
    } else if (hoverIndex > currentIndex) {
      setHoverState({ rows: mcaps.slice(currentIndex + 1, hoverIndex + 1), type: 'buy' })
    } else {
      setHoverState({ rows: [], type: null })
    }
  }, [visibleSortedMcapsRef])

  const handleRowLeave = useCallback(() => {
    setHoverState({ rows: [], type: null })
  }, [])

  // --- Y-axis magnifier ---
  const handleYAxisMouseMove = useStableCallback((e) => {
    // currentTarget = the .y-axis-body this handler is bound to (THIS
    // chart's). document.querySelector('.y-axis-body') returned the FIRST
    // such element in the document — always chart 1's — so with two charts
    // every y-axis interaction resolved to chart 1's node, breaking chart
    // 1's own y-axis behavior. Scoped to the instance now.
    const yAxis = e.currentTarget
    if (!yAxis) return
    const rect = yAxis.getBoundingClientRect()
    // No `+ yAxis.scrollTop` term. .y-axis-body is overflow:visible —
    // styles.css documents that as REQUIRED (the count+1 pad row must not
    // be clipped, or translateY can't slide it into view) — and a box with
    // overflow:visible has no scrollport, so its scrollTop is structurally
    // 0. FPCC's transform block reached the same conclusion independently:
    // "the y-axis-body scrollTop syncs are separate no-ops (overflow:visible
    // cannot scroll)". The read was buying a forced layout flush to add
    // zero; getBoundingClientRect above already flushed, so this was the
    // second flush of the pair.
    //
    // This handler stays SYNCHRONOUS on purpose. Coalescing it to a frame
    // was tried and reverted: magnifierMcapRef below is not React state —
    // it is read directly, in the same tick, by the y-axis pointerdown at
    // FootprintChartCanvas ~6567, which places an order at
    // `magnifierMcapRef.current || mcap`. Deferring the write by one frame
    // means a click arriving in the same frame as the last move prices the
    // order at the PREVIOUS position — several 24px rows away at speed.
    // A wrong-price order is not a trade worth making for a layout read.
    const localY = e.clientY - rect.top
    const mcaps = visibleSortedMcapsRef.current
    const RH = dynamicRowHeightRef.current
    if (mcaps.length < 2 || RH <= 0) {
      setMagnifier(m => m.visible ? { visible: false, y: 0, mcap: 0, axisLeft: 0, axisWidth: 0 } : m)
      return
    }

    const exactMcap = yToMcap(localY, mcaps, RH)
    // Inside the lattice = inside its rows: y in [0, n*RH]. (Was a ±half
    // bucket price window, which only matched the old row-centre mapping.)
    if (!(localY >= 0 && localY <= mcaps.length * RH)) {
      setMagnifier(m => m.visible ? { visible: false, y: 0, mcap: 0, axisLeft: 0, axisWidth: 0 } : m)
      return
    }

    setMagnifier({ visible: true, y: e.clientY, mcap: exactMcap, axisLeft: rect.left, axisWidth: rect.width })
    magnifierMcapRef.current = exactMcap
  })

  const handleYAxisMouseLeave = useStableCallback(() => {
    setMagnifier({ visible: false, y: 0, mcap: 0, axisLeft: 0, axisWidth: 0 })
  })

  const handleChartMouseEnter = useStableCallback(() => { setIsHoveringChart(true) })
  const handleChartMouseLeave = useStableCallback(() => { setIsHoveringChart(false) })

  // ────────────────────────────────────────────────────────────
  // Live AVWAP polyline rebuild — mirrors FootprintChartCanvas draw loop
  // (single source of truth for AVWAP geometry; use this for hover AND context menu)
  // ────────────────────────────────────────────────────────────
  const computeAvwapPointsLive = useCallback((vwap) => {
    const candles = chartData.candles
    const mcaps = visibleSortedMcapsRef.current
    const RH = dynamicRowHeightRef.current
    if (!candles || candles.length === 0 || mcaps.length < 2 || RH <= 0) return []

    // Anchor by time (same as draw)
    let anchorIdx = -1
    for (let i = 0; i < candles.length; i++) {
      if (candles[i].startTime >= vwap.anchorTime) { anchorIdx = i; break }
    }
    if (anchorIdx === -1) return []

    const total = candles.length
    const winSize = viewWindowSizeRef.current
    const plotWidth = Math.max(1, containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
    const pxPerCandle = plotWidth / Math.max(1, winSize)
    // Viewport math must match FootprintChartCanvas.jsx exactly — same
    // pair of padding constants (AUTO_PADDING in autoscroll mode,
    // FUTURE_PADDING for the upper clamp otherwise). The chart draws
    // candles at x-positions derived from these constants; the
    // interaction layer hit-tests against those drawn positions. The
    // old `autoOffset = 3` literal predated centralizing the constants
    // and now disagrees with the chart, mapping every mouseX to the
    // wrong candle.
    const endIdx = resolveEndIdx(viewEndIndexRef.current, total)
    const startIdx = startIdxFor(endIdx, winSize)
    const visibleCount = endIdx - startIdx + 1
    const fracOffset = fractionalOffsetPxRef?.current || 0

    let cumPV = 0, cumV = 0
    const points = []
    // Same point set as the draw loop: the ANCHOR itself (even when it has
    // scrolled off-left, so points[0] is the true anchor x, never the first
    // visible bar), then every bar from the one crossing the left edge
    // (startIdx - 1) through endIdx. The handle and its hit-test both key off
    // points[0], so it stays pinned to the line start instead of sliding.
    const visStart = Math.max(startIdx - 1, anchorIdx)
    const keep = (i) => (i === anchorIdx && anchorIdx < visStart) || (i >= visStart && i <= endIdx)
    for (let i = anchorIdx; i < candles.length; i++) {
      const c = candles[i]
      let vol = 0, price = 0
      if (c.isHistorical) {
        vol = c.volume_tokens || 0
        if (vwap.mode === 'high') price = c.high_mcap
        else if (vwap.mode === 'low') price = c.low_mcap
        else price = (c.open_mcap + c.high_mcap + c.low_mcap + c.close_mcap) / 4
      } else {
        let highMcap = -Infinity, lowMcap = Infinity, weightedMcap = 0
        forEachLevel(c, (mcap, buy, sell) => {
          const levelVol = buy + sell
          if (levelVol === 0) return
          if (mcap > highMcap) highMcap = mcap
          if (mcap < lowMcap) lowMcap = mcap
          weightedMcap += mcap * levelVol; vol += levelVol
        })
        if (vwap.mode === 'high') price = highMcap === -Infinity ? 0 : highMcap
        else if (vwap.mode === 'low') price = lowMcap === Infinity ? 0 : lowMcap
        else price = vol > 0 ? weightedMcap / vol : 0
      }

      if (vol === 0 || price === 0) {
        if (cumV > 0 && keep(i)) {
          const logIdx = i - startIdx; const fromR = visibleCount - 1 - logIdx
          points.push({
            x: plotWidth - (fromR + 0.5) * pxPerCandle + fracOffset,
            y: mcapToY(cumPV / cumV, mcaps, RH),
          })
        }
        continue
      }
      cumPV += price * vol; cumV += vol
      if (keep(i)) {
        const logIdx = i - startIdx; const fromR = visibleCount - 1 - logIdx
        points.push({
          x: plotWidth - (fromR + 0.5) * pxPerCandle + fracOffset,
          y: mcapToY(cumPV / cumV, mcaps, RH),
        })
      }
    }
    return points
  }, [chartData.candles, visibleSortedMcapsRef, dynamicRowHeightRef, viewWindowSizeRef, containerWidthRef, viewEndIndexRef, fractionalOffsetPxRef])

  // Distance from point to polyline segment array — shared by hover + context menu
  const avwapHitTest = useCallback((points, mx, my) => {
    let best = Infinity
    for (let i = 0; i < points.length - 1; i++) {
      const ax = points[i].x, ay = points[i].y
      const bx = points[i + 1].x, by = points[i + 1].y
      const dx = bx - ax, dy = by - ay
      const lenSq = dx * dx + dy * dy
      let t = lenSq > 0 ? ((mx - ax) * dx + (my - ay) * dy) / lenSq : 0
      t = Math.max(0, Math.min(1, t))
      const px = ax + t * dx, py = ay + t * dy
      const dist = Math.sqrt((mx - px) ** 2 + (my - py) ** 2)
      if (dist < best) best = dist
    }
    return best
  }, [])

  // Distance from the pointer to an AVWAP's ANCHOR (points[0]) — the only
  // grabbable part of the line. The polyline hit-test above still exists for
  // the context menu (clicking anywhere on a line to open its settings is
  // harmless — it moves nothing), but DRAGGING keys off this: grabbing the
  // curve anywhere used to re-anchor the whole study, so brushing the line
  // while panning silently rewrote it. An anchored study should only move by
  // its anchor.
  const avwapAnchorDist = useCallback((points, mx, my) => {
    if (!points || points.length === 0) return Infinity
    return Math.hypot(mx - points[0].x, my - points[0].y)
  }, [])

  // Grab radius. Mouse is precise; a fingertip is ~9mm and covers the whole
  // dot, so touch gets a much larger target than the 4-6px it can see.
  const AVWAP_GRAB_PX = 12
  const AVWAP_GRAB_TOUCH_PX = 26
  // Mouse hover band around the line body (highlight only, not a grab).
  const AVWAP_HOVER_LINE_PX = 6
  // Finger-sized grab band for S/R lines. The mouse path uses 10px, which is
  // a cursor tolerance; a fingertip covers far more than that and cannot be
  // placed to 10px. Slightly under the AVWAP radius because an S/R line is a
  // full-width target — you only need to be close in Y.
  const SR_GRAB_TOUCH_PX = 22

  // --- AVWAP / S/R hover detection ---
  // Coalesced to the frame. A 1000Hz mouse delivers ~16 moves per 60Hz
  // frame and each one read layout (getBoundingClientRect) while the
  // chart's rAF writes transforms every frame — a read after a write forces
  // a synchronous reflow, so the browser flushed layout once per mouse
  // event (`Event: pointerover` 807ms, `getBoundingClientRect` 159ms in the
  // Aug 2026 profile). Bigger payoff here than the flush itself: the loop
  // below rebuilds EVERY AVWAP polyline via computeAvwapPointsLive to
  // hit-test anchors. That was a full geometry rebuild per mouse event;
  // it is now one per frame, which is all the screen can show.
  //
  // Safe to defer, unlike the y-axis magnifier: hoveredAvwapIdRef and
  // hoveredSRLineIdRef are synced FROM React state by the effects at ~74,
  // so they already lagged this computation by a commit before any of
  // this. The glow the user sees renders from that same state — you can
  // only grab what you see glowing, and what you see glowing is what the
  // ref holds. The magnifier's ref is written directly and read by an
  // order-placing pointerdown, which is why that one stayed synchronous.
  const avwapHoverRef = useRef(null)   // {clientX, clientY} — newest only
  const avwapRafRef = useRef(0)

  const applyAvwapHover = useStableCallback(() => {
    avwapRafRef.current = 0
    const p = avwapHoverRef.current
    if (!p) return
    // Re-checked at frame time, not capture time: a drag can begin in the
    // gap between the move event and this frame, and it owns the pointer.
    if (draggingItemRef.current) return

    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const mx = p.clientX - rect.left
    const my = p.clientY - rect.top

    // AVWAP hover — compute live to match rendered polyline (same source as S/R)
    const vwaps = avwapsRef.current
    let closestId = null
    let closestScore = Infinity

    // Hover ANYWHERE on the line (within AVWAP_HOVER_LINE_PX of any drawn
    // segment), or within the anchor's grab radius. Highlight only — drag
    // still arms from the anchor alone (handleOverlayDragStart), and the
    // right-click menu already hit-tests the whole polyline.
    for (const vwap of vwaps) {
      const points = computeAvwapPointsLive(vwap)
      if (points.length < 2) continue
      const dLine = avwapHitTest(points, mx, my)
      const dAnchor = avwapAnchorDist(points, mx, my)
      // Normalise each distance by its own tolerance so the nearest line wins.
      const score = Math.min(dLine / AVWAP_HOVER_LINE_PX, dAnchor / AVWAP_GRAB_PX)
      if (score < 1 && score < closestScore) { closestScore = score; closestId = vwap.id }
    }

    // Ref + one scheduled draw (no-op when unchanged) — no React render.
    setHoveredAvwapId(closestId)

    // S/R hover — unchanged, already live
    const lines = srLinesRef.current
    let hovSRId = null

    if (lines.length > 0) {
      const mcaps = visibleSortedMcapsRef.current
      const RH = dynamicRowHeightRef.current
      if (mcaps.length > 1 && RH > 0) {
        const totalPxHeight = mcaps.length * RH

        for (const line of lines) {
          const lineY = mcapToY(line.mcap, mcaps, RH)
          if (lineY < -2 || lineY > totalPxHeight + 2) continue
          if (Math.abs(my - lineY) < 10) { hovSRId = line.id; break }
        }
      }
    }

    if (hovSRId !== hoveredSRLineIdRef.current) {
      setHoveredSRLineId(hovSRId)
      if (drawCanvasRef.current) drawCanvasRef.current()
    }
  })

  const handleAvwapHover = useStableCallback((e) => {
    // Cheapest early-out stays at event time: during a drag this costs one
    // ref read instead of scheduling a frame that would bail anyway.
    if (draggingItemRef.current) return
    // Coords copied out NOW — never hold the event across a frame.
    avwapHoverRef.current = { clientX: e.clientX, clientY: e.clientY }
    if (!avwapRafRef.current) {
      avwapRafRef.current = requestAnimationFrame(applyAvwapHover)
    }
  })

  // Unmount can land between a move and its frame; without this the
  // callback runs against a torn-down chart.
  useEffect(() => () => {
    if (avwapRafRef.current) cancelAnimationFrame(avwapRafRef.current)
    avwapRafRef.current = 0
  }, [])

  // --- Reset interaction state on symbol change ---
  // The chart is not remounted when the symbol changes (no key={token}), so
  // everything below survives a switch unless it is explicitly cleared.
  // `currentToken` here is FPCC's `effectiveToken`, which is `hl_<BASE>` for
  // futures and the mint for spot — so one dependency covers ETH→BTC,
  // token→token, and spot↔futures alike.
  //
  // The one that matters is magnifierMcapRef. It is not React state; the
  // y-axis pointerdown at FootprintChartCanvas ~6567 reads it in the same
  // tick and places an order at `magnifierMcapRef.current || mcap`. `||`
  // rejects 0 but NOT a stale non-zero, so a pointer already parked on the
  // axis when the symbol changes — no mousemove to refresh it — would price
  // the next click against the PREVIOUS symbol's scale. A $6M mcap carried
  // onto an $80k token is not a near-miss, it is an order at a price that
  // does not exist on this chart. Zeroing hands the fallback back to `mcap`
  // (the row actually clicked), which is the correct answer until the next
  // move recomputes a precise one.
  //
  // The hover ids are cosmetic by comparison — a glow left over from another
  // symbol's overlays — but they are cleared here for the same reason, and
  // the pending AVWAP frame is dropped so it cannot land a hit-test computed
  // against the outgoing symbol.
  useEffect(() => {
    if (avwapRafRef.current) {
      cancelAnimationFrame(avwapRafRef.current)
      avwapRafRef.current = 0
    }
    avwapHoverRef.current = null
    magnifierMcapRef.current = 0
    setMagnifier(m => m.visible ? { visible: false, y: 0, mcap: 0, axisLeft: 0, axisWidth: 0 } : m)
    setHoverState(s => s.type === null ? s : { rows: [], type: null })
    setHoveredAvwapId(null)
    setHoveredSRLineId(null)
  }, [currentToken])

  // --- Drag start: mousedown on hovered AVWAP or S/R line ---
  const handleOverlayDragStart = useStableCallback((e) => {
    if (e.button !== 0) return false
    const touch = e.pointerType === 'touch'
    const canvas0 = canvasRef.current
    if (touch && canvas0) {
      const rect = canvas0.getBoundingClientRect()
      const mx = e.clientX - rect.left
      const my = e.clientY - rect.top
      const plotWidth = Math.max(1, containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)

      const srAnchorX = (line) => {
        const candles = chartData.candles
        if (!candles || !candles.length || !line || !line.anchorTime) return 0
        if (!(line.ray || line.style === 'ray')) return 0
        let ai = -1
        for (let ci = candles.length - 1; ci >= 0; ci--) {
          if ((candles[ci] && candles[ci].startTime || 0) <= line.anchorTime) { ai = ci; break }
        }
        if (ai < 0) return 0
        const total = candles.length
        const px = plotWidth / Math.max(1, viewWindowSizeRef.current)
        const endIdx = resolveEndIdx(viewEndIndexRef.current, total)
        const frac = (fractionalOffsetPxRef && fractionalOffsetPxRef.current) || 0
        return plotWidth - (endIdx - ai + 1) * px + frac + (line.anchorFrac || 0) * px
      }

      const armPhone = (drag) => {
        const prev = draggingItemRef.current
        if (prev && prev.menuTimer) clearTimeout(prev.menuTimer)
        drag.menuTimer = setTimeout(() => {
          if (draggingItemRef.current !== drag || drag.moved) return
          draggingItemRef.current = null
          setIsDraggingOverlay(false)
          if (drag.type === 'avwap') {
            const vwap = avwapsRef.current.find(v => v.id === drag.id)
            if (vwap) setAvwapSettings({ show: true, x: drag.x0, y: drag.y0, vwap })
          } else if (drag.type === 'sr') {
            const line = srLinesRef.current.find(l => l.id === drag.id)
            if (line) {
              setSrLineSettings({ show: true, x: drag.x0, y: drag.y0, line })
              hoveredSRLineIdRef.current = drag.id
              setHoveredSRLineId(drag.id)
            }
          }
          if (drawCanvasRef.current) drawCanvasRef.current()
        }, 450)
        draggingItemRef.current = drag
        setIsDraggingOverlay(true)
        if (drawCanvasRef.current) drawCanvasRef.current()
      }

      let hitId = null
      let hitDist = 22
      let onHandle = false
      for (const vwap of avwapsRef.current) {
        const points = computeAvwapPointsLive(vwap)
        if (points.length < 2) continue
        const d = avwapHitTest(points, mx, my)
        if (d < hitDist) {
          hitDist = d
          hitId = vwap.id
          onHandle = avwapAnchorDist(points, mx, my) <= PHONE_HANDLE_HIT
        }
      }
      if (hitId !== null) {
        const vwap = avwapsRef.current.find(v => v.id === hitId)
        setHoveredAvwapId(hitId)
        if (hoveredSRLineIdRef.current != null) {
          hoveredSRLineIdRef.current = null
          setHoveredSRLineId(null)
        }
        armPhone({
          type: 'avwap', id: hitId, originalMode: (vwap && vwap.mode) || 'vwap',
          x0: e.clientX, y0: e.clientY, moved: false, phone: true, onHandle,
        })
        return true
      }

      const mcaps = visibleSortedMcapsRef.current
      const RH = dynamicRowHeightRef.current
      let srId = null
      let srOnHandle = false
      let srBest = 22
      if (mcaps.length > 1 && RH > 0) {
        const totalPxHeight = mcaps.length * RH
        for (const line of srLinesRef.current) {
          const lineY = mcapToY(line.mcap, mcaps, RH)
          if (lineY < -2 || lineY > totalPxHeight + 2) continue
          const ray = !!(line.ray || line.style === 'ray')
          const sx = ray ? srAnchorX(line) : 0
          if (ray && mx < sx - PHONE_HANDLE_HIT) continue
          const dy = Math.abs(my - lineY)
          if (dy < srBest) {
            srBest = dy
            srId = line.id
            srOnHandle = Math.hypot(mx - clampHandleX(sx, plotWidth), my - lineY) <= PHONE_HANDLE_HIT
          }
        }
      }
      if (srId !== null) {
        hoveredSRLineIdRef.current = srId
        setHoveredSRLineId(srId)
        if (hoveredAvwapIdRef.current != null) {
          setHoveredAvwapId(null)
        }
        armPhone({
          type: 'sr', id: srId,
          x0: e.clientX, y0: e.clientY, moved: false, phone: true, onHandle: srOnHandle,
        })
        return true
      }

      let cleared = false
      if (hoveredSRLineIdRef.current != null) {
        hoveredSRLineIdRef.current = null
        setHoveredSRLineId(null)
        cleared = true
      }
      if (hoveredAvwapIdRef.current != null) {
        setHoveredAvwapId(null)
        cleared = true
      }
      if (cleared && drawCanvasRef.current) drawCanvasRef.current()
      return false
    }


    const hovSR = hoveredSRLineIdRef.current

    // AVWAP: hit-test the anchor from THIS event's coordinates rather than
    // reading hoveredAvwapIdRef. Two reasons, and the second is the whole
    // phone story: (1) the anchor is the only grab handle now, and (2) touch
    // has no hover — a finger's first contact IS the pointerdown, so a
    // hover-gated drag could never arm on a phone at all. Measuring here
    // works identically for both input types; the radius is what differs.
    const canvas = canvasRef.current
    if (canvas) {
      const rect = canvas.getBoundingClientRect()
      const mx = e.clientX - rect.left
      const my = e.clientY - rect.top
      const grab = e.pointerType === 'touch' ? AVWAP_GRAB_TOUCH_PX : AVWAP_GRAB_PX
      let hitId = null, hitDist = grab
      for (const vwap of avwapsRef.current) {
        const points = computeAvwapPointsLive(vwap)
        if (points.length < 2) continue
        const d = avwapAnchorDist(points, mx, my)
        if (d < hitDist) { hitDist = d; hitId = vwap.id }
      }
      if (hitId !== null) {
        const vwap = avwapsRef.current.find(v => v.id === hitId)
        draggingItemRef.current = { type: 'avwap', id: hitId, originalMode: vwap?.mode || 'vwap' }
        // Reflect the grab immediately: on touch nothing set hover, and the
        // draw loop reads this ref for the anchor's grabbed styling.
        if (hoveredAvwapIdRef.current !== hitId) setHoveredAvwapId(hitId)
        setIsDraggingOverlay(true)
        return true
      }
    }

    // S/R: same problem the AVWAP branch above already solves, and the same
    // fix. Arming from hoveredSRLineIdRef works on a mouse, where hover is
    // continuously maintained and cleared. On touch there IS no hover — the
    // id gets set once and nothing ever clears it (no mouseleave), so every
    // later pointerdown ANYWHERE re-armed the drag on that line. That is the
    // "can't let go of it" symptom: releasing worked fine, the next touch
    // just grabbed it again.
    //
    // So hit-test from this event's own coordinates on touch, with a finger-
    // sized radius, and require an actual hit. Mouse keeps the hover path.
    let srId = hovSR
    if (e.pointerType === 'touch') {
      srId = null
      const cv = canvasRef.current
      const mcaps = visibleSortedMcapsRef.current
      const RH = dynamicRowHeightRef.current
      if (cv && mcaps.length > 1 && RH > 0) {
        const r = cv.getBoundingClientRect()
        const ty = e.clientY - r.top
        const totalPxHeight = mcaps.length * RH
        let best = SR_GRAB_TOUCH_PX
        for (const line of srLinesRef.current) {
          const lineY = mcapToY(line.mcap, mcaps, RH)
          if (lineY < -2 || lineY > totalPxHeight + 2) continue
          const d = Math.abs(ty - lineY)
          if (d < best) { best = d; srId = line.id }
        }
      }
    }

    if (srId !== null) {
      // Touch: defer the real drag until the finger moves past a slop. A still
      // tap (common when you just want the settings sheet) must not begin a
      // reposition — and must open the menu on lift. Mouse keeps immediate
      // drag; it already has right-click / double-click for settings.
      const touch = e.pointerType === 'touch'
      draggingItemRef.current = {
        type: 'sr',
        id: srId,
        x0: e.clientX,
        y0: e.clientY,
        moved: !touch,
      }
      if (hoveredSRLineIdRef.current !== srId) setHoveredSRLineId(srId)
      setIsDraggingOverlay(true)
      return true
    }

    // Touch miss: clear select glow. Arming is hit-tested (above), so a
    // lingering hovered id is display-only now — safe to keep after a grab,
    // but an empty tap must dismiss it or the line looks stuck selected.
    if (e.pointerType === 'touch') {
      if (hoveredSRLineIdRef.current !== null) setHoveredSRLineId(null)
      if (hoveredAvwapIdRef.current !== null) setHoveredAvwapId(null)
    }

    return false
  })

  // --- Drag move: update position in real-time ---
  const handleOverlayDragMove = useStableCallback((e) => {
    const drag = draggingItemRef.current
    if (!drag) return
    // Own the gesture so the phone body (touch-action: pan-y) cannot scroll
    // the Y window under an S/R / AVWAP drag.
    if (e.cancelable) e.preventDefault()

    // Touch S/R: hold still until the finger clears the tap slop, then promote
    // to a real reposition. Until then the line stays put and lift → menu.
    if (drag.phone && !drag.moved) {
      const dx = e.clientX - drag.x0
      const dy = e.clientY - drag.y0
      if (dx * dx + dy * dy < 10 * 10) return
      drag.moved = true
      if (drag.menuTimer) { clearTimeout(drag.menuTimer); drag.menuTimer = 0 }
      // AVWAP only moves by its square. A body swipe must not re-anchor.
      if (drag.type === 'avwap' && !drag.onHandle) {
        draggingItemRef.current = null
        setIsDraggingOverlay(false)
        return
      }
    }

    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top

    if (drag.type === 'avwap') {
      const total = chartData.candles.length
      const winSize = viewWindowSizeRef.current
      const plotWidth = Math.max(1, containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
      const pxPerCandle = plotWidth / Math.max(1, winSize)
      // Same padding constants the chart's draw loop uses — see
      // computeAvwapPointsLive comment for why these must match.
      const endIdx = resolveEndIdx(viewEndIndexRef.current, total)
      const startIdx = startIdxFor(endIdx, winSize)
      const visibleCount = endIdx - startIdx + 1
      // Candle X positions are drawn at `... + fracOffset` (see
      // computeAvwapPointsLive). Subtract it so the drag's candle pick
      // matches the visible polyline during autoscroll micro-scroll.
      const fracOffset = fractionalOffsetPxRef?.current || 0
      const logicalIdx = visibleCount - Math.ceil((plotWidth - mouseX + fracOffset) / pxPerCandle)
      const candleIdx = Math.max(startIdx, Math.min(total - 1, startIdx + logicalIdx))

      let newMode = drag.originalMode || 'vwap'
      const mcaps = visibleSortedMcapsRef.current
      const RH = dynamicRowHeightRef.current
      if (mcaps.length > 1 && RH > 0) {
        const c = chartData.candles[candleIdx]
        if (c) {
          let hi = -Infinity, lo = Infinity
          if (c.isHistorical) { hi = c.high_mcap; lo = c.low_mcap }
          else {
            forEachLevel(c, (mcap, buy, sell) => {
              if ((buy + sell) === 0) return
              if (mcap > hi) hi = mcap
              if (mcap < lo) lo = mcap
            })
          }
          if (hi > -Infinity && lo < Infinity) {
            // Row-based snap, not raw-mcap snap. The old check was
            // `mouseMcap >= hi` / `mouseMcap <= lo` — strict, against
            // the candle's actual high/low price. But `mouseMcap` from
            // yToMcap is the CENTER of whichever row the cursor is in,
            // and the candle's high can sit anywhere inside its top
            // row. If the high is in the upper half of a row and the
            // cursor in the lower half of that same row, mouseMcap < hi
            // and the snap silently fails — exactly what "not sticking
            // to highs/lows" looks like. Compare rows instead: if the
            // cursor is in the same row as hi (or any row above), snap
            // to 'high'; mirror for lo. This makes the entire top and
            // bottom regions of the candle's footprint "sticky."
            // Rows by CELL membership (latticeRowIndex — the row whose
            // cell holds the price, log-aware), not floor((top-p)/B),
            // which named the row above the cell.
            if (mcaps.length >= 2) {
              const hiRow    = latticeRowIndex(mcaps, hi)
              const loRow    = latticeRowIndex(mcaps, lo)
              const mouseRow = Math.floor(mouseY / RH)
              if      (mouseRow <= hiRow) newMode = 'high'
              else if (mouseRow >= loRow) newMode = 'low'
              else                        newMode = 'vwap'
            }
          }
        }
      }

      setAvwaps(prev => prev.map(v => v.id === drag.id ? {
        ...v, anchorIdx: candleIdx, anchorTime: chartData.candles[candleIdx]?.startTime || 0, mode: newMode,
      } : v))
    } else if (drag.type === 'sr') {
      const mcaps = visibleSortedMcapsRef.current
      const RH = dynamicRowHeightRef.current
      if (mcaps.length > 1 && RH > 0) {
        const newMcap = yToMcap(mouseY, mcaps, RH)
        const line = srLinesRef.current.find(l => l.id === drag.id)
        const ray = !!(line && (line.ray || line.style === 'ray'))
        // Phone: left/right start lives on the square. Body drag stays vertical.
        // Desktop ray drags (checkbox or legacy style) still move the anchor.
        const moveStart = ray && (drag.phone ? !!drag.onHandle : true)
        if (moveStart) {
          const total = chartData.candles.length
          const winSize = viewWindowSizeRef.current
          const plotWidth = Math.max(1, containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
          const pxPerCandle = plotWidth / Math.max(1, winSize)
          // Same padding constants the chart's draw loop uses.
          const endIdx = resolveEndIdx(viewEndIndexRef.current, total)
          const startIdx = startIdxFor(endIdx, winSize)
          const visibleCount = endIdx - startIdx + 1
          // Match AVWAP drag: undo the per-frame fractional X shift the
          // drawing applies, so the picked anchor candle matches the
          // visible line during autoscroll micro-scroll.
          const fracOffset = fractionalOffsetPxRef?.current || 0
          const logicalIdx = visibleCount - Math.ceil((plotWidth - mouseX + fracOffset) / pxPerCandle)
          const candleIdx = Math.max(startIdx, Math.min(total - 1, startIdx + logicalIdx))
          const newAnchorTime = chartData.candles[candleIdx]?.startTime || line.anchorTime
          const newAnchorFrac = srAnchorFracAt(mouseX, candleIdx, total, plotWidth, pxPerCandle, endIdx, fracOffset)
          setSrLines(prev => prev.map(l => l.id === drag.id ? { ...l, mcap: newMcap, anchorTime: newAnchorTime, anchorFrac: newAnchorFrac } : l))
        } else {
          setSrLines(prev => prev.map(l => l.id === drag.id ? { ...l, mcap: newMcap } : l))
        }
      }
    }

    if (drawCanvasRef.current) drawCanvasRef.current()
  })

  // --- Drag end: commit final position ---
  const handleOverlayDragEnd = useStableCallback(() => {
    const drag = draggingItemRef.current
    if (!drag) return
    if (drag.menuTimer) clearTimeout(drag.menuTimer)
    draggingItemRef.current = null
    setIsDraggingOverlay(false)

    if (drag.type === 'sr') {
      // Still finger = tap → open settings (phone has no right-click; double-
      // tap is easy to miss once select/drag owns the first contact).
      if (drag.moved && currentToken) {
        // Price before the drag, from storage (the drag only touched state).
        let oldMcap = null
        try {
          const prevLines = JSON.parse(localStorage.getItem(`solcheddar_srlines_${currentToken}`) || '[]')
          oldMcap = prevLines.find(l => l.id === drag.id)?.mcap ?? null
        } catch {}
        const moved = srLinesRef.current.find(l => l.id === drag.id)
        saveSRLinesLocal(currentToken, srLinesRef.current)
        if (moved && Number.isFinite(oldMcap) && moved.mcap !== oldMcap) srLineMovedRef?.current?.(oldMcap, moved.mcap)
      }
      // Keep hoveredSRLineId after release so the line STAYS selected on
      // phone (glow / settings affordance). Arming no longer reads this id
      // on touch — it hit-tests — so a lingering hover cannot re-grab the
      // line on the next tap. Empty-tap clear lives in drag-start's miss path.
    }

    if (drawCanvasRef.current) drawCanvasRef.current()
  })

  // --- Window listeners for overlay drag ---
  useEffect(() => {
    if (!isDraggingOverlay) return
    // pointermove must be non-passive: preventDefault in the move handler is
    // what keeps phone body scroll (touch-action: pan-y) from stealing Y.
    window.addEventListener('pointermove', handleOverlayDragMove, { passive: false })
    window.addEventListener('pointerup', handleOverlayDragEnd)
    window.addEventListener('pointercancel', handleOverlayDragEnd)
    return () => {
      window.removeEventListener('pointermove', handleOverlayDragMove)
      window.removeEventListener('pointerup', handleOverlayDragEnd)
      window.removeEventListener('pointercancel', handleOverlayDragEnd)
    }
  }, [isDraggingOverlay, handleOverlayDragMove, handleOverlayDragEnd])

  // --- Chart context menu ---
  const handleChartContextMenu = useStableCallback((e) => {
    if (e.target.closest('.y-axis-body')) return
    if (draggingItemRef.current) { e.preventDefault(); return }
    e.preventDefault()

    if (chartData.candles.length === 0) {
      setContextMenu({ show: true, x: e.clientX, y: e.clientY, candleIdx: -1, clickMcap: 0 })
      return
    }

    const body = bodyScrollRef.current
    const canvas = canvasRef.current
    if (!body || !canvas) return
    const rect = canvas.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const mouseYCanvas = e.clientY - rect.top

    const total = chartData.candles.length
    const winSize = viewWindowSizeRef.current
    const plotWidth = Math.max(1, containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
    const pxPerCandle = plotWidth / Math.max(1, winSize)
    // Same padding constants the chart's draw loop uses.
    const endIdx = resolveEndIdx(viewEndIndexRef.current, total)
    const startIdx = startIdxFor(endIdx, winSize)
    const visibleCount = endIdx - startIdx + 1
    const fracOffCtx = fractionalOffsetPxRef?.current || 0
    const logicalIdx = visibleCount - Math.ceil((plotWidth - mouseX + fracOffCtx) / pxPerCandle)
    const candleIdx = Math.max(startIdx, Math.min(total - 1, startIdx + logicalIdx))
    const anchorFrac = srAnchorFracAt(mouseX, candleIdx, total, plotWidth, pxPerCandle, endIdx, fracOffCtx)

    // AVWAP proximity — compute live (same source as hover)
    const vwaps = avwapsRef.current
    for (const vwap of vwaps) {
      const points = computeAvwapPointsLive(vwap)
      if (points.length < 2) continue
      if (avwapHitTest(points, mouseX, mouseYCanvas) < 15) {
        setAvwapSettings({ show: true, x: e.clientX, y: e.clientY, vwap })
        return
      }
    }

    // Resolve mcap at click position
    const RH_ctx = dynamicRowHeightRef.current
    const sortedMcaps_ctx = visibleSortedMcapsRef.current
    let clickMcap = 0
    if (sortedMcaps_ctx.length > 1 && RH_ctx > 0) {
      clickMcap = yToMcap(mouseYCanvas, sortedMcaps_ctx, RH_ctx)
    }

    // S/R proximity
    if (sortedMcaps_ctx.length > 1 && srLinesRef.current.length > 0) {
      const RH_sr = dynamicRowHeightRef.current
      for (const line of srLinesRef.current) {
        const lineY = mcapToY(line.mcap, sortedMcaps_ctx, RH_sr)
        // 10px is a mouse hover; phone double-tap is fat-finger. Match the
        // touch grab radius so "select / open settings" actually lands.
        const srHit = 22
        if (Math.abs(mouseYCanvas - lineY) < srHit) {
          setSrLineSettings({ show: true, x: e.clientX, y: e.clientY, line })
          setHoveredSRLineId(line.id)
          return
        }
      }
    }

    if (candleIdx >= startIdx && candleIdx <= endIdx) {
      setContextMenu({ show: true, x: e.clientX, y: e.clientY, candleIdx, clickMcap, anchorFrac })
    }
  })

  // --- AVWAP add/update/delete ---
  const handleAddAVWAP = useCallback(() => {
    const idx = contextMenu.candleIdx
    setContextMenu({ show: false, x: 0, y: 0, candleIdx: -1 })
    if (idx < 0) return
    const id = nextAvwapId.current++
    const saved = (() => { try { return JSON.parse(localStorage.getItem('solcheddar_avwap_defaults')) } catch { return null } })()
    setAvwaps(prev => [...prev, {
      id, anchorIdx: idx,
      anchorTime: chartData.candles[idx]?.startTime || 0,
      color: saved?.color || '#ffaa00',
      weight: saved?.weight || 2,
      mode: saved?.mode || 'vwap',
    }])
  }, [contextMenu.candleIdx, chartData.candles])

  const handleUpdateAVWAP = useCallback((updated) => {
    setAvwaps(prev => prev.map(v => v.id === updated.id ? updated : v))
    try { localStorage.setItem('solcheddar_avwap_defaults', JSON.stringify({ color: updated.color, weight: updated.weight, mode: updated.mode })) } catch {}
  }, [])

  const handleDeleteAVWAP = useCallback((id) => {
    setAvwaps(prev => prev.filter(v => v.id !== id))
    setAvwapSettings({ show: false, x: 0, y: 0, vwap: null })
  }, [])

  // Where the cursor sat inside the anchor candle, in candle widths, so a
  // ray starts under the cursor rather than snapping to the candle's left edge.
  const srAnchorFracAt = (mouseX, candleIdx, total, plotWidth, px, endIdx, frac) => {
    const left = plotWidth - (endIdx - candleIdx + 1) * px + (frac || 0)
    const f = (mouseX - left) / Math.max(1e-6, px)
    return Math.max(0, candleIdx >= total - 1 ? f : Math.min(1, f))
  }

  // --- S/R line add/update/delete ---
  const handleAddSRLine = useCallback(() => {
    const mcap = contextMenu.clickMcap
    const candleIdx = contextMenu.candleIdx
    setContextMenu({ show: false, x: 0, y: 0, candleIdx: -1, clickMcap: 0 })
    if (!mcap || !currentToken) return
    const id = nextSrLineId.current++
    const saved = (() => { try { return JSON.parse(localStorage.getItem('solcheddar_srline_defaults')) } catch { return null } })()
    const newLine = {
      id, mcap,
      color: saved?.color || '#ff6b6b',
      style: saved?.style || 'dashed',
      weight: saved?.weight || 1,
      // Ray is remembered like colour/style/weight: the last S/R line's
      // choice carries to the next one.
      ray: !!saved?.ray,
      label: '',
      anchorTime: chartData.candles[candleIdx]?.startTime || Date.now(),
      anchorFrac: contextMenu.anchorFrac || 0,
    }
    const updated = [...srLinesRef.current, newLine]
    saveSRLinesLocal(currentToken, updated)
  }, [contextMenu.clickMcap, contextMenu.candleIdx, currentToken, saveSRLinesLocal, chartData.candles])

  const handleUpdateSRLine = useCallback((updated) => {
    const before = srLinesRef.current.find(l => l.id === updated.id)
    if (before && Number.isFinite(before.mcap) && Number.isFinite(updated.mcap) && before.mcap !== updated.mcap) {
      srLineMovedRef?.current?.(before.mcap, updated.mcap)
    }
    const lines = srLinesRef.current.map(l => l.id === updated.id ? updated : l)
    saveSRLinesLocal(currentToken, lines)
    // ray included: it was the one menu setting left out of the remembered
    // defaults, so every new S/R line came back without it.
    try { localStorage.setItem('solcheddar_srline_defaults', JSON.stringify({ color: updated.color, style: updated.style, weight: updated.weight, ray: !!(updated.ray || updated.style === 'ray') })) } catch {}
  }, [currentToken, saveSRLinesLocal, srLineMovedRef])

  // Grok drawing bridge (utils/grokDraw.js). Same models + persistence as the
  // context-menu adds, but explicit values and NO remembered-defaults write.
  const grokAddSR = useCallback(({ mcap, color, ray, label, anchorTime }) => {
    const token = srTokenRef.current
    if (!token || !(mcap > 0)) return null
    const id = nextSrLineId.current++
    const line = { id, mcap, color: color || '#4fc3f7', style: 'dashed', weight: 1, ray: !!ray, label: label || '', anchorTime: anchorTime || Date.now(), by: 'grok' }
    saveSRLinesLocal(token, [...srLinesRef.current, line])
    return id
  }, [saveSRLinesLocal])

  const grokAddAVWAP = useCallback(({ anchorIdx, color }) => {
    const c = chartData.candles?.[anchorIdx]
    if (!c) return null
    const id = nextAvwapId.current++
    setAvwaps(prev => [...prev, { id, anchorIdx, anchorTime: c.startTime || 0, color: color || '#ffaa00', weight: 2, mode: 'vwap', by: 'grok' }])
    return id
  }, [chartData.candles])

  const handleDeleteSRLine = useCallback((id) => {
    const lines = srLinesRef.current.filter(l => l.id !== id)
    saveSRLinesLocal(currentToken, lines)
    setSrLineSettings({ show: false, x: 0, y: 0, line: null })
  }, [currentToken, saveSRLinesLocal])

  return {
    // AVWAP state
    avwaps, setAvwaps,
    avwapsRef,
    // hoveredAvwapId kept as a snapshot for any legacy reader; NOT reactive.
    hoveredAvwapId: hoveredAvwapIdRef.current, hoveredAvwapIdRef,
    avwapPointsCacheRef, // legacy export — no longer read for hit-testing, safe to drop later
    avwapSettings, setAvwapSettings,

    // S/R state
    srLines, setSrLines,
    srLinesRef,
    srLineSettings, setSrLineSettings,
    hoveredSRLineId, hoveredSRLineIdRef,

    // Drag state
    draggingItemRef,
    isDraggingOverlay,

    // Context menu
    contextMenu, setContextMenu,

    // Hover / magnifier
    hoverState,
    magnifier,
    magnifierMcapRef,

    // Chart hover
    isHoveringChart,
    handleChartMouseEnter,
    handleChartMouseLeave,

    // Actions
    loadSRLines,
    handleRowHover,
    handleRowLeave,
    handleYAxisMouseMove,
    handleYAxisMouseLeave,
    handleAvwapHover,
    handleOverlayDragStart,
    handleOverlayDragMove,
    handleOverlayDragEnd,
    handleChartContextMenu,
    handleAddAVWAP,
    handleUpdateAVWAP,
    handleDeleteAVWAP,
    handleAddSRLine,
    handleUpdateSRLine,
    handleDeleteSRLine,
    grokAddSR, grokAddAVWAP,
  }
}
